<script setup lang="ts">
import ServiceCard from "~/components/ui/ServiceCard.vue";
import Step from "~/components/ui/Step.vue";
import Review from "~/components/ui/Review.vue";
import Faq from "~/components/ui/Faq.vue";
import {
  useSeoBase,
  useJsonLd,
  orgNode,
  localBusinessNode,
  websiteNode,
  faqNode,
} from "../composables/useSeo";
import { useLocations } from "~/composables/useLocations";
import { usePhoneNumber } from "../composables/usePhoneNumber";
import { useAnalytics } from "../composables/useAnalytics";

// Get phone number
const { phoneNumber, phoneNumberPretty, phoneNumberTel } = usePhoneNumber();
const { trackConversion } = useAnalytics();

// Get centralized service areas data
const { cities: serviceAreas } = useLocations();

// FAQ data for schema
const faqs = [
  {
    q: "Do you offer same-day junk removal in Minneapolis?",
    a: "Yes! We offer same-day junk removal service in Minneapolis, St. Paul, and many surrounding Twin Cities areas when you call early in the day. Availability depends on our schedule, so call (612) 424-0609 to check same-day availability for your location.",
  },
  {
    q: "What items can you haul away?",
    a: "We haul away furniture, appliances, electronics, mattresses, yard waste, construction debris, hot tubs, and general household junk. We accept most items except hazardous materials like paint, chemicals, asbestos, and certain electronics that require special disposal.",
  },
  {
    q: "Do you provide light demolition and cleanup?",
    a: "Absolutely. We specialize in light demolition including shed teardowns, deck removal, garage cleanouts, and small structure demolition throughout Minneapolis and St. Paul. Our crew handles the demo work, hauls away all debris, and leaves your property broom-clean.",
  },
  {
    q: "How does pricing work?",
    a: "Our pricing is based on the volume of space your items take up in our truck (1/4, 1/2, 3/4, or full load). We provide upfront estimates before starting work-no surprises. Factors include volume, weight, access difficulty, and demolition complexity.",
  },
  {
    q: "Do I need to be home for pickup?",
    a: "Not necessarily. If items are accessible (like curbside, driveway, or garage) and we have clear instructions, you don't need to be present. For interior items or demolition work, we typically need someone on-site to grant access and confirm the scope.",
  },
  {
    q: "Do you remove sheds, decks, and garages?",
    a: "Yes. We handle shed demolition, deck removal, and garage cleanouts across the Twin Cities. Our licensed crew safely tears down structures, hauls away all debris, and performs final site cleanup.",
  },
  {
    q: "What areas do you serve?",
    a: "We serve Minneapolis, St. Paul, Bloomington, Eden Prairie, Maple Grove, Brooklyn Park, Lakeville, and surrounding Twin Cities suburbs. Same-day service is often available in our core service areas.",
  },
];

useSeoBase({
  title: "Junk Removal Minneapolis & St. Paul | Same-Day | Junk Guys MN",
  description:
    "Same-day junk removal and full demolition in Minneapolis - St. Paul. Call Junk Guys MN for fast, local hauling and cleanouts.",
  canonical: "https://junkguysmn.com",
  ogImage: "https://junkguysmn.com/og.jpg",
});
useJsonLd([orgNode(), localBusinessNode(), websiteNode(), faqNode(faqs)]);
</script>

<template>
  <div>
    <!-- HERO -->
    <section class="relative overflow-hidden bg-slate-900 text-white">
      <div class="absolute inset-0 opacity-20">
        <img
          src="/images/hero.webp"
          alt="Crew loading truck"
          class="h-full w-full object-cover"
        />
      </div>
      <div class="relative mx-auto max-w-7xl px-4 py-24 lg:py-32">
        <div class="max-w-2xl">
          <h1 class="text-4xl font-extrabold tracking-tight sm:text-5xl">
            Same-Day Junk Removal & Full Demolition in Minneapolis - St. Paul
          </h1>

          <!-- SEO Hero Paragraph -->
          <p class="mt-6 text-lg text-slate-100 leading-relaxed">
            Professional junk removal and demolition throughout Minneapolis, St.
            Paul, and the Twin Cities. From furniture and appliances to shed
            teardowns and deck removal, our licensed crew handles it all. Call
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="font-bold text-white underline hover:text-slate-100"
              >(612) 424-0609</a
            >
            or
            <NuxtLink
              to="/book"
              class="font-bold text-white underline hover:text-slate-100"
              >book online</NuxtLink
            >
            for same-day service.
          </p>

          <p class="mt-4 text-lg text-slate-200">
            Transparent pricing. Responsible disposal. Friendly crew. Get your
            space back today.
          </p>
          <div class="mt-8 flex flex-wrap gap-3">
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="inline-flex items-center justify-center rounded-md bg-white px-5 py-3 font-semibold text-slate-900 shadow hover:bg-slate-100"
            >
              Call {{ phoneNumberPretty }}
            </a>
            <NuxtLink
              to="/book"
              class="inline-flex items-center justify-center rounded-md bg-primary-main px-5 py-3 font-semibold text-white shadow hover:bg-primary-dark"
            >
              Book Online
            </NuxtLink>
            <NuxtLink
              to="/pricing"
              class="inline-flex items-center justify-center rounded-md border border-white/30 px-5 py-3 font-semibold text-white hover:bg-white/10"
            >
              See Pricing
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- SERVICES -->
    <section class="bg-slate-50">
      <div class="mx-auto max-w-7xl px-4 py-14">
        <h2 class="text-3xl font-bold text-slate-900">
          Junk Removal Services in the Twin Cities
        </h2>
        <p class="mt-2 text-lg text-slate-600">
          From single items to full property cleanouts, we handle it all in
          Minneapolis, St. Paul, and surrounding areas.
        </p>
        <div class="mt-8 grid gap-6 grid-cols-2 lg:grid-cols-3">
          <ServiceCard
            title="Junk Removal"
            to="/services/junk-removal"
            copy="Full-service hauling for any household or business junk"
            img="/images/services/junk.webp"
          />
          <ServiceCard
            title="Appliance Removal"
            to="/services/appliance-removal"
            copy="Safe disposal of refrigerators, washers, dryers, and more"
            img="/images/services/appliance-removal.webp"
          />
          <ServiceCard
            title="Shed Demolition"
            to="/services/shed-demolition"
            copy="Full teardown and debris removal for any shed size"
            img="/images/services/shed-removal.webp"
          />
          <ServiceCard
            title="Garage Cleanout"
            to="/services/garage-cleanout"
            copy="Complete garage clearing with donation and recycling options"
            img="/images/services/garage-cleanout.webp"
          />
          <ServiceCard
            title="Hot Tub Removal"
            to="/services/hot-tub-removal"
            copy="Expert hot tub demolition and responsible disposal"
            img="/images/services/hottub-removal.webp"
          />
          <ServiceCard
            title="Construction Debris"
            to="/services/construction-debris"
            copy="Drywall, flooring, lumber, and renovation waste removal"
            img="/images/services/construction-debris.webp"
          />
          <ServiceCard
            title="Furniture Removal"
            to="/services/furniture-removal"
            copy="Couches, mattresses, tables - we haul it all away"
            img="/images/services/furniture-removal.webp"
          />
          <ServiceCard
            title="Deck Removal"
            to="/services/deck-removal"
            copy="Licensed deck demolition with complete site cleanup"
            img="/images/services/deck-removal.webp"
          />
        </div>
      </div>
    </section>

    <!-- FULL DEMOLITION -->
    <section class="bg-white">
      <div class="mx-auto max-w-7xl px-4 py-14">
        <h2 class="text-3xl font-bold text-slate-900">
          Full Demolition Services (Homes, Sheds, Decks & More)
        </h2>
        <p class="mt-2 text-lg text-slate-600">
          Beyond junk removal, we handle complete demolition projects of all
          sizes across the Twin Cities. From small backyard structures to full
          home takedowns, we tear it down, haul it away, and leave your property
          clean and ready for your next project.
        </p>

        <div class="mt-8 grid gap-8 md:grid-cols-3">
          <div class="bg-slate-50 p-6 rounded-lg border border-slate-200">
            <h3 class="text-xl font-bold text-slate-900 mb-3">
              Residential Home Demolition
            </h3>
            <p class="text-slate-600">
              Complete home teardowns for renovations or new construction. We
              handle structural demolition, debris removal, and site
              preparation. Fully licensed and insured for major residential
              projects throughout Minneapolis and St. Paul.
            </p>
          </div>

          <div class="bg-slate-50 p-6 rounded-lg border border-slate-200">
            <h3 class="text-xl font-bold text-slate-900 mb-3">
              Outdoor Structure Demolition
            </h3>
            <p class="text-slate-600">
              Professional demolition of sheds, decks, gazebos, pergolas, and
              other outdoor structures. Safe removal of wooden or metal
              structures with complete debris hauling and site cleanup.
            </p>
          </div>

          <div class="bg-slate-50 p-6 rounded-lg border border-slate-200">
            <h3 class="text-xl font-bold text-slate-900 mb-3">
              Garage & Large Structure Removal
            </h3>
            <p class="text-slate-600">
              Full garage demolition, pole barns, workshops, and large accessory
              structures. We handle complex teardowns including concrete
              foundations, electrical disconnection, and complete site
              restoration.
            </p>
          </div>
        </div>

        <div
          class="mt-6 p-4 bg-primary-main/10 border border-primary-main/20 rounded-lg"
        >
          <p class="text-slate-700">
            <strong>Note:</strong> All demolition work performed by fully
            licensed and insured professionals. We handle all necessary permits,
            utility disconnections, and ensure safe, compliant teardowns for
            projects of any size throughout the Twin Cities area.
          </p>
        </div>
      </div>
    </section>

    <!-- HOW IT WORKS -->
    <section class="bg-white">
      <div class="mx-auto max-w-7xl px-4 py-14">
        <h2 class="text-3xl font-bold text-slate-900">
          How Our Same-Day Pickup Works
        </h2>
        <p class="mt-2 text-lg text-slate-600">
          Three simple steps to reclaim your space-often same day in Minneapolis
          and St. Paul.
        </p>
        <div class="mt-8 grid gap-6 md:grid-cols-3">
          <div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">
              1. Fast Estimate
            </h3>
            <Step num="1" title="Fast Estimate"
              >Text a photo or book an on-site quote.</Step
            >
          </div>
          <div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">
              2. We Haul & Sweep
            </h3>
            <Step num="2" title="We Haul & Sweep"
              >On time, careful removal, broom-clean finish.</Step
            >
          </div>
          <div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">
              3. Responsible Disposal
            </h3>
            <Step num="3" title="Responsible Disposal"
              >Donate/recycle when possible; receipts on request.</Step
            >
          </div>
        </div>
      </div>
    </section>

    <!-- PRICING FACTORS -->
    <section class="bg-white border-y border-slate-200">
      <div class="mx-auto max-w-7xl px-4 py-14">
        <h2 class="text-3xl font-bold text-slate-900 text-center mb-3">
          What Affects Junk Removal Pricing?
        </h2>
        <p class="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
          Our transparent pricing is based on these key factors:
        </p>

        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <!-- Content Column -->
          <div>
            <ul class="space-y-4">
              <li class="flex items-start">
                <svg
                  class="w-6 h-6 text-primary-main mr-3 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div>
                  <strong class="text-slate-900">Volume & Truck Space:</strong>
                  <span class="text-slate-600">
                    The amount of space your items take up in our truck (1/4
                    load, 1/2 load, 3/4 load, or full load)</span
                  >
                </div>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-6 h-6 text-primary-main mr-3 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div>
                  <strong class="text-slate-900">Weight & Item Type:</strong>
                  <span class="text-slate-600">
                    Heavy materials like concrete, appliances, or hot tubs
                    require special handling</span
                  >
                </div>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-6 h-6 text-primary-main mr-3 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div>
                  <strong class="text-slate-900">Access & Labor:</strong>
                  <span class="text-slate-600">
                    Stairs, tight spaces, or challenging removal locations may
                    affect pricing</span
                  >
                </div>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-6 h-6 text-primary-main mr-3 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div>
                  <strong class="text-slate-900">Demolition Complexity:</strong>
                  <span class="text-slate-600">
                    Sheds, decks, and garages vary based on size, materials, and
                    teardown difficulty</span
                  >
                </div>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-6 h-6 text-primary-main mr-3 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div>
                  <strong class="text-slate-900">Same-Day Availability:</strong>
                  <span class="text-slate-600">
                    Need it gone today? We offer same-day service in Minneapolis
                    and St. Paul when available</span
                  >
                </div>
              </li>
            </ul>

            <div
              class="mt-8 p-6 bg-primary-main/10 border-l-4 border-primary-main rounded-r-lg"
            >
              <p class="text-lg text-slate-900">
                <strong>Ready for an accurate quote?</strong> Call
                <a
                  :href="phoneNumberTel"
                  @click="trackConversion"
                  class="font-bold text-primary-main hover:text-primary-dark"
                  >(612) 424-0609</a
                >
                for a fast estimate. We provide upfront pricing before we
                start-no surprises.
              </p>
            </div>
          </div>

          <!-- Image Column -->
          <div class="relative">
            <div class="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/images/pricing-estimate.webp"
                alt="Professional crew member providing transparent pricing estimate with clipboard"
                class="w-full h-full object-cover"
              />
            </div>
            <div
              class="absolute -bottom-4 -right-4 bg-primary-main text-white p-4 rounded-lg shadow-lg"
            >
              <p class="text-sm font-semibold">Volume-Based Pricing</p>
              <p class="text-2xl font-bold">Fair & Transparent</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- REVIEWS -->
    <section class="bg-slate-50">
      <div class="mx-auto max-w-7xl px-4 py-14">
        <h2 class="text-3xl font-bold text-slate-900">
          Why Twin Cities Homeowners Choose Junk Guys MN
        </h2>
        <p class="mt-2 text-lg text-slate-600">
          Real reviews from our Google Business Profile.
        </p>
        <div class="mt-8 grid gap-6 md:grid-cols-3">
          <Review author="Adam M." city="Minneapolis">
            I'm always looking for no headache and a complete job when it comes
            to junk removal, and these guys truly set the bar when it comes to
            that. They helped me plan out demos and did the entire demolition,
            and have done several junk removal jobs for me around this city at
            this point. I've been recommending them for months will continue to
            work with them for all my jobs.
          </Review>
          <Review author="Greg Grillo" city="Twin Cities">
            Today I called Junk Guys to see if they could remove some items from
            my home. Mattress sets and other odds and ends needed to go. They
            arrived early, the same day, just 2 hours later! Al and Nick were
            awesome! They are polite, professional, caring, and extremely
            helpful. This is a great company and I will be sharing their contact
            information with everyone! The price was great!
          </Review>
          <Review author="Scooter Draughn" city="Twin Cities">
            These guys are on POINT! I have a non-profit that does a lot of food
            distribution. They come through and make sure EVERYTHING is cleaned!
            From pallets, to boxes, waste, bad produce, and more, they take care
            of it. HIGHLY RECOMMEND!
          </Review>
        </div>

        <div class="mt-10 text-center">
          <a
            href="https://share.google/0HM2yYXOKKuRYsHh2"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-primary-main text-primary-main font-semibold rounded-lg hover:bg-primary-main hover:text-white transition-colors shadow-md"
          >
            <svg
              class="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            View More Reviews on Google
          </a>
        </div>
      </div>
    </section>

    <!-- LOCAL TRUST SECTION -->
    <section class="bg-white border-y border-slate-200">
      <div class="mx-auto max-w-7xl px-4 py-14">
        <div class="text-center mb-10">
          <h2 class="text-3xl font-bold text-slate-900">
            Why Choose Junk Guys MN?
          </h2>
          <p class="mt-2 text-lg text-slate-600">
            Locally owned and trusted throughout the Twin Cities
          </p>
        </div>

        <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div
            class="text-center p-6 bg-slate-50 rounded-lg border border-slate-200"
          >
            <div
              class="w-16 h-16 mx-auto mb-4 bg-primary-main/10 rounded-full flex items-center justify-center"
            >
              <svg
                class="w-8 h-8 text-primary-main"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-slate-900 mb-2">
              Locally Owned & Operated
            </h3>
            <p class="text-sm text-slate-600">
              Family-owned business serving Minneapolis and St. Paul communities
              since day one.
            </p>
          </div>

          <div
            class="text-center p-6 bg-slate-50 rounded-lg border border-slate-200"
          >
            <div
              class="w-16 h-16 mx-auto mb-4 bg-primary-main/10 rounded-full flex items-center justify-center"
            >
              <svg
                class="w-8 h-8 text-primary-main"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-slate-900 mb-2">
              Same-Day Availability
            </h3>
            <p class="text-sm text-slate-600">
              Need it gone today? We offer same-day pickup in Minneapolis and
              St. Paul when you call early.
            </p>
          </div>

          <div
            class="text-center p-6 bg-slate-50 rounded-lg border border-slate-200"
          >
            <div
              class="w-16 h-16 mx-auto mb-4 bg-primary-main/10 rounded-full flex items-center justify-center"
            >
              <svg
                class="w-8 h-8 text-primary-main"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-slate-900 mb-2">
              Licensed & Insured
            </h3>
            <p class="text-sm text-slate-600">
              Professional crew with full licensing and insurance for your peace
              of mind.
            </p>
          </div>

          <div
            class="text-center p-6 bg-slate-50 rounded-lg border border-slate-200"
          >
            <div
              class="w-16 h-16 mx-auto mb-4 bg-primary-main/10 rounded-full flex items-center justify-center"
            >
              <svg
                class="w-8 h-8 text-primary-main"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-slate-900 mb-2">
              Responsible Disposal
            </h3>
            <p class="text-sm text-slate-600">
              We donate and recycle whenever possible. Careful handling of your
              property guaranteed.
            </p>
          </div>
        </div>

        <div
          class="mt-10 text-center p-6 bg-primary-main/5 border border-primary-main/20 rounded-lg"
        >
          <p class="text-lg text-slate-900">
            <strong>Top-rated by local customers</strong> across Minneapolis,
            St. Paul, and the Twin Cities metro.
            <NuxtLink
              to="/gallery"
              class="text-primary-main hover:text-primary-dark font-semibold underline"
              >See our work</NuxtLink
            >
            or
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="text-primary-main hover:text-primary-dark font-semibold underline"
              >call (612) 424-0609</a
            >
            for a free quote.
          </p>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="bg-white">
      <div class="mx-auto max-w-7xl px-4 py-14">
        <h2 class="text-3xl font-bold text-slate-900">
          Frequently Asked Questions
        </h2>
        <p class="mt-2 text-lg text-slate-600">
          Common questions about junk removal and demolition services in the
          Twin Cities
        </p>
        <div class="mt-6 divide-y divide-slate-200 rounded-lg border">
          <Faq q="Do you offer same-day junk removal in Minneapolis?">
            Yes! We offer same-day junk removal service in Minneapolis, St.
            Paul, and many surrounding Twin Cities areas when you call early in
            the day. Availability depends on our schedule, so call
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >(612) 424-0609</a
            >
            to check same-day availability for your location.
          </Faq>
          <Faq q="What items can you haul away?">
            We haul away furniture, appliances, electronics, mattresses, yard
            waste, construction debris, hot tubs, and general household junk. We
            accept most items except hazardous materials like paint, chemicals,
            asbestos, and certain electronics that require special disposal.
            Call us if you're unsure about a specific item.
          </Faq>
          <Faq q="Do you provide light demolition and cleanup?">
            Absolutely. We specialize in light demolition including shed
            teardowns, deck removal, garage cleanouts, and small structure
            demolition throughout Minneapolis and St. Paul. Our crew handles the
            demo work, hauls away all debris, and leaves your property
            broom-clean.
          </Faq>
          <Faq q="How does pricing work?">
            Our pricing is based on the volume of space your items take up in
            our truck (1/4, 1/2, 3/4, or full load). We provide upfront
            estimates before starting work-no surprises. Factors include volume,
            weight, access difficulty, and demolition complexity. Single items
            often have flat rates.
          </Faq>
          <Faq q="Do I need to be home for pickup?">
            Not necessarily. If items are accessible (like curbside, driveway,
            or garage) and we have clear instructions, you don't need to be
            present. For interior items or demolition work, we typically need
            someone on-site to grant access and confirm the scope.
          </Faq>
          <Faq q="Do you remove sheds, decks, and garages?">
            Yes. We handle shed demolition, deck removal, and garage cleanouts
            across the Twin Cities. Our licensed crew safely tears down
            structures, hauls away all debris, and performs final site cleanup.
            We handle permits when required for demolition projects.
          </Faq>
          <Faq q="What areas do you serve?">
            We serve Minneapolis, St. Paul, Bloomington, Eden Prairie, Maple
            Grove, Brooklyn Park, Lakeville, and surrounding Twin Cities
            suburbs. Same-day service is often available in our core service
            areas. Call to confirm coverage for your specific location.
          </Faq>
        </div>
      </div>
    </section>

    <!-- SERVICE AREAS -->
    <section class="bg-slate-50">
      <div class="mx-auto max-w-7xl px-4 py-14">
        <h2 class="text-3xl font-bold text-slate-900">Service Areas</h2>
        <p class="mt-2 text-lg text-slate-600">
          Professional junk removal and demolition throughout
          <strong
            >Minneapolis, St. Paul and surrounding Twin Cities suburbs</strong
          >. We do not claim statewide coverage-we focus on delivering
          exceptional local service.
        </p>

        <div class="mt-8 p-6 bg-white rounded-lg border border-slate-200">
          <h3 class="text-xl font-bold text-slate-900 mb-4">
            Core Service Cities
          </h3>
          <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <NuxtLink
              to="/city/minneapolis"
              class="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-primary-main hover:bg-primary-main/5 transition-colors font-semibold text-slate-900 hover:text-primary-main text-center"
            >
              Minneapolis
            </NuxtLink>
            <NuxtLink
              to="/city/saint-paul"
              class="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-primary-main hover:bg-primary-main/5 transition-colors font-semibold text-slate-900 hover:text-primary-main text-center"
            >
              Saint Paul
            </NuxtLink>
            <NuxtLink
              to="/city/bloomington"
              class="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-primary-main hover:bg-primary-main/5 transition-colors font-semibold text-slate-900 hover:text-primary-main text-center"
            >
              Bloomington
            </NuxtLink>
            <NuxtLink
              to="/city/eden-prairie"
              class="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-primary-main hover:bg-primary-main/5 transition-colors font-semibold text-slate-900 hover:text-primary-main text-center"
            >
              Eden Prairie
            </NuxtLink>
            <NuxtLink
              to="/city/maple-grove"
              class="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-primary-main hover:bg-primary-main/5 transition-colors font-semibold text-slate-900 hover:text-primary-main text-center"
            >
              Maple Grove
            </NuxtLink>
            <NuxtLink
              to="/city/brooklyn-park"
              class="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-primary-main hover:bg-primary-main/5 transition-colors font-semibold text-slate-900 hover:text-primary-main text-center"
            >
              Brooklyn Park
            </NuxtLink>
            <NuxtLink
              to="/city/lakeville"
              class="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-primary-main hover:bg-primary-main/5 transition-colors font-semibold text-slate-900 hover:text-primary-main text-center"
            >
              Lakeville
            </NuxtLink>
          </div>
        </div>

        <div class="mt-6">
          <h3 class="text-lg font-bold text-slate-900 mb-3">
            Additional Twin Cities Suburbs
          </h3>
          <p class="text-slate-600 mb-4">
            Same-day availability in many areas-call to check.
          </p>
          <div
            class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3"
          >
            <NuxtLink
              v-for="city in serviceAreas.filter(
                (c) =>
                  ![
                    'minneapolis',
                    'saint-paul',
                    'bloomington',
                    'eden-prairie',
                    'maple-grove',
                    'brooklyn-park',
                    'lakeville',
                  ].includes(c),
              )"
              :key="city"
              :to="`/city/${city}`"
              class="text-center p-3 bg-white rounded-lg border border-slate-200 hover:border-primary-light hover:bg-primary-main/5 transition-all duration-200 text-slate-700 hover:text-primary-main font-medium capitalize"
            >
              {{ city.replace(/-/g, " ") }}
            </NuxtLink>
          </div>
        </div>

        <div class="mt-8 text-center">
          <p class="text-slate-600 mb-4">
            Looking for specific services in your area?
          </p>
          <div class="flex flex-wrap justify-center gap-3">
            <NuxtLink
              to="/services"
              class="inline-flex items-center px-5 py-2.5 bg-primary-main text-white font-semibold rounded-md hover:bg-primary-dark transition-colors"
            >
              View All Services
            </NuxtLink>
            <NuxtLink
              to="/city/minneapolis"
              class="inline-flex items-center px-5 py-2.5 bg-white border border-slate-300 text-slate-900 font-semibold rounded-md hover:border-primary-main hover:text-primary-main transition-colors"
            >
              Junk Removal Minneapolis
            </NuxtLink>
            <NuxtLink
              to="/city/saint-paul"
              class="inline-flex items-center px-5 py-2.5 bg-white border border-slate-300 text-slate-900 font-semibold rounded-md hover:border-primary-main hover:text-primary-main transition-colors"
            >
              Junk Removal Saint Paul
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- FINAL CTA -->
    <section class="bg-primary-main text-white">
      <div class="mx-auto max-w-7xl px-4 py-12 text-center">
        <h2 class="text-3xl font-bold">Get a Free Quote Today</h2>
        <p class="text-xl text-white mb-8">
          Call now or book online in under 2 minutes.
        </p>
        <div class="mt-6 flex flex-wrap justify-center gap-3">
          <a
            :href="phoneNumberTel"
            class="rounded-md bg-white px-5 py-3 font-semibold text-primary-main hover:bg-secondary-dark"
          >
            📞 Call {{ phoneNumberPretty }}
          </a>
          <NuxtLink
            to="/book"
            class="rounded-md border border-white/30 px-5 py-3 font-semibold hover:bg-white/10"
          >
            📅 Book Online
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>
